import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createAProcurement,
  editAProcurement,
  getAProcurement,
  getBusinessTypes,
  getIndustries,
} from '../../services/procurementService';

import FormikInput from '../../components/CustomTextInput/CustomTextInput';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import FormikTextarea from '../../components/CustomTextArea/CustomTextArea';
import FormikSelect from '../../components/CustomDropdownSelect/CustomDropdownSelect';
import FormikDateInput from '../../components/CustomDatePicker/CustomDatePicker';
import { ProcurementDataProps } from '../../types/procurement-types';
import { toast } from 'react-toastify';
import { ProcurementStatus } from '../../types/enums';
import { useTranslation } from 'react-i18next';
import { formatDate } from '../../utils/helper';

interface FormValues {
  procurementProjectId?: string;
  status?: number;
  title?: string;
  industryId?: string;
  businessType?: string;
  bidStartDate?: string | Date;
  bidEndDate?: string | Date;
  description?: string;
  bidRequirement?: string;
  prerequisite?: string;
  additionalInformation?: string;
}

interface Industry {
  industryId: string;
  industryName: string;
}

interface LookupType {
  lookupId: string;
  lookupName: string;
  lookupValue: string;
}

interface ServiceInformationProps {
  goToNextTab: () => void;
  serviceInformationId: string | null;
  setServiceInformationId: React.Dispatch<React.SetStateAction<string | null>>;
  procurementWorkflow: boolean;
}

const ServiceInformation: React.FC<ServiceInformationProps> = ({
  goToNextTab,
  serviceInformationId,
  setServiceInformationId,
  procurementWorkflow = false,
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [saveAndContinue, setSaveAndContinue] = useState(false);

  // Fetch industries using react-query
  const { data: industries, error: industriesError } = useQuery<Industry[]>({
    queryKey: ['industries'],
    queryFn: async () => {
      const response = await getIndustries();
      return response.data;
    },
    staleTime: 3 * 60 * 1000, // Cache data for 3 minutes
    refetchOnWindowFocus: false, // Do not refetch when the window regains focus
  });

  // Fetch business types using react-query
  const { data: businessTypes, error: businessTypesError } = useQuery<LookupType[]>({
    queryKey: ['businessTypes'],
    queryFn: async () => {
      const response = await getBusinessTypes();
      return response.data;
    },
    staleTime: 3 * 60 * 1000, // Cache data for 3 minutes
    refetchOnWindowFocus: false, // Do not refetch when the window regains focus
  });

  // Fetch procurement project data if serviceInformationId exists
  const { data: procurementProjectData, error: procurementError } = useQuery<ProcurementDataProps>({
    queryKey: ['procurementProject', serviceInformationId],
    queryFn: async () => {
      const response = await getAProcurement(serviceInformationId!);
      return response.data;
    },
    enabled: !!serviceInformationId,
    staleTime: 3 * 60 * 1000, // Cache data for 3 minutes
    refetchOnWindowFocus: false, // Do not refetch when the window regains focus
  });

  // Handle errors locally
  React.useEffect(() => {
    if (industriesError) {
      toast.error(t('errorFetchingIndustries'));
    }
    if (businessTypesError) {
      toast.error(t('errorFetchingBusinessTypes'));
    }
    if (procurementError) {
      toast.error(t('errorFetchingProcurement'));
    }
  }, [industriesError, businessTypesError, procurementError]);

  // Mutation for creating or editing a procurement
  const { mutate: saveProcurement, isPending: isSaving } = useMutation({
    mutationFn: (values: FormValues) =>
      serviceInformationId ? editAProcurement(values) : createAProcurement(values),
    onSuccess: (response) => {
      toast.success(response.data.message);
      setServiceInformationId(response.data.id);
      queryClient.invalidateQueries({ queryKey: ['procurementProject'] });

      if (saveAndContinue) {
        goToNextTab();
      } else {
        navigate(-1);
      }
    },
    onError: () => {
      toast.error('An error occurred while saving the procurement');
    },
  });

  const validationSchema = Yup.object().shape({
    title: Yup.string().required(t('serviceInfoValidation.titleRequired')),
    industryId: Yup.string().required(t('serviceInfoValidation.industryRequired')),
    businessType: Yup.string().required(t('serviceInfoValidation.businessTypeRequired')),
    bidStartDate: Yup.date()
      .required(t('serviceInfoValidation.bidStartDateRequired'))
      .min(new Date(), t('serviceInfoValidation.bidStartDatePast')),
    bidEndDate: Yup.date()
      .required(t('serviceInfoValidation.bidEndDateRequired'))
      .min(Yup.ref('bidStartDate'), t('serviceInfoValidation.bidEndDateAfterStart')),
    description: Yup.string().required(t('serviceInfoValidation.descriptionRequired')),
    bidRequirement: Yup.string().required(t('serviceInfoValidation.bidRequirementRequired')),
    prerequisite: Yup.string().required(t('serviceInfoValidation.prerequisiteRequired')),
    additionalInformation: Yup.string().required(
      t('serviceInfoValidation.additionalInformationRequired')
    ),
  });

  const handleSubmit = async (values: FormValues) => {
    if (procurementWorkflow) {
      // In workflow mode, just navigate without saving
      goToNextTab();
      return;
    }

    const payload = {
      ...values,
      bidStartDate: formatDate(values.bidStartDate),
      bidEndDate: formatDate(values.bidEndDate),
      ...(serviceInformationId && { serviceInformationId }),
      workReference: '',
      status: ProcurementStatus.DRAFT,
    };

    console.log('values: ', values);
    console.log('payload: ', payload);

    saveProcurement(payload);
  };

  return (
    <div>
      <div className="mb-8 border-b border-b-[#E5E5E5] pb-4">
        <h2 className="text-2xl font-semibold">
          {t('provideServiceDetails', 'Provide service details')}
        </h2>
      </div>

      <Formik<FormValues>
        initialValues={{
          title: procurementProjectData?.title || '',
          industryId: procurementProjectData?.industryId || '',
          businessType: procurementProjectData?.businessType || '',
          bidStartDate: procurementProjectData?.bidStartDate
            ? new Date(procurementProjectData?.bidStartDate)
            : '',
          bidEndDate: procurementProjectData?.bidEndDate
            ? new Date(procurementProjectData?.bidEndDate)
            : '',
          description: procurementProjectData?.description || '',
          bidRequirement: procurementProjectData?.bidRequirement || '',
          prerequisite: procurementProjectData?.prerequisite || '',
          additionalInformation: procurementProjectData?.additionalInformation || '',
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ setFieldValue, values }) => (
          <Form>
            <FormikInput
              label={t('titleOfProcurement')}
              name="title"
              type="text"
              placeholder={t('titlePlaceholder')}
              disabled={procurementWorkflow || isSaving}
              isRequired
            />

            <FormikSelect
              label={t('industry')}
              name="industryId"
              options={
                industries?.map((industry) => ({
                  value: industry.industryId,
                  label: industry.industryName,
                })) || []
              }
              disabled={procurementWorkflow || isSaving}
              isRequired
            />

            <FormikSelect
              label={t('supplierType')}
              name="businessType"
              options={
                businessTypes?.map((businessType) => ({
                  value: businessType.lookupValue,
                  label: businessType.lookupName,
                })) || []
              }
              disabled={procurementWorkflow || isSaving}
              isRequired
            />

            <div className="flex w-full gap-x-4">
              <FormikDateInput
                label={t('biddingStartDate')}
                name="bidStartDate"
                disabled={procurementWorkflow || isSaving}
                isRequired
              />
              <FormikDateInput
                label={t('biddingEndDate')}
                name="bidEndDate"
                disabled={procurementWorkflow || isSaving}
                isRequired
              />
            </div>

            <FormikTextarea
              label={t('description')}
              name="description"
              placeholder="Describe the project"
              disabled={procurementWorkflow || isSaving}
              isRequired
            />

            <FormikTextarea
              label={t('bidRequirement')}
              name="bidRequirement"
              placeholder={t('bidRequirementPlaceholder')}
              disabled={procurementWorkflow || isSaving}
              isRequired
            />

            <FormikTextarea
              label={t('serviceInfoPrerequisites')}
              name="prerequisite"
              placeholder={t('serviceInfoPrerequisitesPlaceholder')}
              disabled={procurementWorkflow || isSaving}
              isRequired
            />

            <FormikTextarea
              label={t('additionalInformation')}
              name="additionalInformation"
              placeholder={t('additionalInformationPlaceholder')}
              disabled={procurementWorkflow || isSaving}
              isRequired
            />

            {procurementWorkflow ? (
              <div className="ml-auto mt-10 flex w-full items-center justify-end gap-x-[18px]">
                <button
                  type="button"
                  className="min-w-[195px] rounded-[10px] bg-[#FFC726] px-[22px] py-3.5 text-base font-semibold text-[#101828] transition-colors duration-300 hover:bg-[#FFB600]"
                  onClick={() => goToNextTab()}
                >
                  {t('continue')}
                </button>
              </div>
            ) : (
              <div className="ml-auto mt-10 flex w-full items-center justify-end gap-x-[18px]">
                <button
                  type="submit"
                  className="min-w-[195px] rounded-[10px] bg-[#F5F5F5] px-[22px] py-3.5 text-base font-semibold text-[#101828] transition-colors duration-300 hover:bg-[#E0E0E0] disabled:cursor-not-allowed disabled:bg-[#D6D6D6] disabled:text-[#B0B0B0]"
                  disabled={isSaving}
                  onClick={() => setSaveAndContinue(false)}
                >
                  {t('saveAndExit')}
                </button>
                <button
                  type="submit"
                  className="min-w-[195px] rounded-[10px] bg-[#FFC726] px-[22px] py-3.5 text-base font-semibold text-[#101828] transition-colors duration-300 hover:bg-[#FFB600] disabled:cursor-not-allowed disabled:bg-[#E0A900] disabled:text-[#B0B0B0]"
                  disabled={isSaving}
                  onClick={() => setSaveAndContinue(true)}
                >
                  {t('saveAndContinue')}
                </button>
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ServiceInformation;
